import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'src/redux/redux-store';
import { invisiblyApi } from 'src/redux/api/invisibly-api';
import type { TrendsData } from 'src/pages/results/endpoints/results.type';
import 'src/pages/results/endpoints/trends.endpoints';

export function useSurveyGroupTrendsData(extSurveyIds: string[], interval: number): {
  trendsBySurvey: Partial<Record<string, TrendsData>>;
  isLoading: boolean;
  hasError: boolean;
} {
  const dispatch = useDispatch<AppDispatch>();
  const [trendsBySurvey, setTrendsBySurvey] = useState<Partial<Record<string, TrendsData>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const idsKey = useMemo(() => [...extSurveyIds].sort().join(','), [extSurveyIds]);

  useEffect(() => {
    if (!extSurveyIds.length) {
      setTrendsBySurvey({});
      setHasError(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setHasError(false);

    const pending = extSurveyIds.map((ext_survey_id) =>
      dispatch(
        invisiblyApi.endpoints.getTrendsData.initiate({
          ext_survey_id,
          interval,
          selected_survey_answers: [],
        })
      )
    );

    void Promise.allSettled(pending.map((p) => p.unwrap())).then((results) => {
      if (cancelled) {
        return;
      }
      const next: Partial<Record<string, TrendsData>> = {};
      let anyErr = false;
      extSurveyIds.forEach((id, i) => {
        const r = results[i];
        if (r.status === 'fulfilled') {
          next[id] = r.value;
        } else {
          anyErr = true;
        }
      });
      setTrendsBySurvey(next);
      setHasError(anyErr);
    }).finally(() => {
      if (!cancelled) {
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
      pending.forEach((p) => p.abort());
    };
  }, [dispatch, idsKey, interval, extSurveyIds]);

  return { trendsBySurvey, isLoading, hasError };
}

